import { CurvePath, LineCurve3, QuadraticBezierCurve3 } from '@anov/3d-core'
import type { Vector3 } from '@anov/3d-core'
import type { RoundedCornersCurveInterface } from '.'

const calculateDistance = (p1: Vector3, p2: Vector3): number => {
  return p1.clone().sub(p2).length()
}

/**
 * calculateSafeRadius
 * @param points
 * @param radius
 * @returns
 */
const calculateSafeRadius = (points: Vector3[], radius: number): number[] => {
  if (points.length <= 2)
    return [radius, radius]

  const safeRadii: number[] = new Array(points.length).fill(radius)

  for (let i = 1; i < points.length - 1; i++) {
    const prevDist = calculateDistance(points[i], points[i - 1])
    const nextDist = calculateDistance(points[i], points[i + 1])

    const maxSafeRadius = Math.min(prevDist / 2, nextDist / 2, radius)
    safeRadii[i] = maxSafeRadius
  }

  if (points.length > 2) {
    const firstDist = calculateDistance(points[0], points[1])
    const lastDist = calculateDistance(points[points.length - 1], points[points.length - 2])

    safeRadii[0] = Math.min(firstDist / 2, radius)
    safeRadii[points.length - 1] = Math.min(lastDist / 2, radius)
  }

  return safeRadii
}

/**
 * 创建圆角拐角曲线
 * @param points
 * @param radius
 * @param close
 * @returns
 */
export const createRoundedCornersCurve = (points: Vector3[], radius = 20, close = true) => {
  const curve = new CurvePath() as CurvePath<Vector3>

  if (points.length < 2)
    throw new Error('At least two points are required to create a curve')

  if (points.length === 2) {
    curve.curves.push(new LineCurve3(points[0], points[1]))
    return curve
  }

  const safeRadii = calculateSafeRadius(points, radius)

  const lines = []
  const lastIndex = points.length - 1

  if (close && points.length > 2) {
    const firstLastDist = calculateDistance(points[0], points[lastIndex])
    const safeConnectRadius = Math.min(firstLastDist / 2, radius)
    safeRadii[0] = Math.min(safeRadii[0], safeConnectRadius)
    safeRadii[lastIndex] = Math.min(safeRadii[lastIndex], safeConnectRadius)
  }

  if (points.length > 2) {
    const dir = points[0].clone().sub(points[1]).normalize()
    const realEndPoints = points[1].clone().add(dir.multiplyScalar(safeRadii[1]))

    let firstPoint = points[0]
    if (close)
      firstPoint = points[0].clone().add(points[1].clone().sub(points[0]).normalize().multiplyScalar(safeRadii[0]))

    lines.push(new LineCurve3(firstPoint, realEndPoints))
  }

  for (let i = 1; i < lastIndex; i++) {
    const dir1 = points[i - 1].clone().sub(points[i]).normalize()
    const dir2 = points[i + 1].clone().sub(points[i]).normalize()
    const dir3 = points[i].clone().sub(points[i + 1]).normalize()

    const p1 = points[i].clone().add(dir1.multiplyScalar(safeRadii[i]))
    const p2 = points[i].clone()
    const p3 = points[i].clone().add(dir2.multiplyScalar(safeRadii[i]))

    const bezierCurve = new QuadraticBezierCurve3(p1, p2, p3)
    lines.push(bezierCurve)

    const nextRadius = safeRadii[i + 1]
    const nextEndPoint = (i === lastIndex - 1 && close)
      ? points[i + 1].clone().add(dir3.multiplyScalar(nextRadius))
      : (i === lastIndex - 1)
          ? points[i + 1]
          : points[i + 1].clone().add(dir3.multiplyScalar(nextRadius))

    lines.push(new LineCurve3(p3, nextEndPoint))
  }

  if (close && points.length > 2) {
    const lastDir1 = points[0].clone().sub(points[lastIndex]).normalize()
    const lastDir2 = points[lastIndex - 1].clone().sub(points[lastIndex]).normalize()

    const lastP1 = points[lastIndex].clone().add(lastDir2.multiplyScalar(safeRadii[lastIndex]))
    const lastP2 = points[lastIndex].clone()
    const lastP3 = points[lastIndex].clone().add(lastDir1.multiplyScalar(safeRadii[lastIndex]))

    const lastBezier = new QuadraticBezierCurve3(lastP1, lastP2, lastP3)
    lines.push(lastBezier)

    const firstDir1 = points[1].clone().sub(points[0]).normalize()
    const firstDir2 = points[lastIndex].clone().sub(points[0]).normalize()

    const firstP1 = points[0].clone().add(firstDir2.multiplyScalar(safeRadii[0]))
    const firstP2 = points[0].clone()
    const firstP3 = points[0].clone().add(firstDir1.multiplyScalar(safeRadii[0]))

    lines.push(new LineCurve3(lastP3, firstP1))
    lines.push(new QuadraticBezierCurve3(firstP1, firstP2, firstP3))
  }

  curve.curves = [...lines]
  return curve
}

const recreateCurve = (target: any, _propertyKey: string | symbol, descriptor: PropertyDescriptor) => {
  const originalMethod = descriptor.value

  const handleFunction = (fn: Function) => function (...args: any[]) {
    const res = fn.apply(this, args)
    this.curves = createRoundedCornersCurve(this._points, this._radius, this._close).curves
    return res
  }

  if (originalMethod)
    descriptor.value = handleFunction(originalMethod)
  else
    descriptor.set = handleFunction(descriptor.set)

  return descriptor
}

/**
 * 圆角拐角曲线类
 */
export class RoundedCornersCurve extends CurvePath<Vector3> implements RoundedCornersCurveInterface {
  private _points: Vector3[]
  private _radius = 20
  private _close = true

  get points(): Vector3[] {
    return this._points.slice()
  }

  @recreateCurve
  set points(points: Vector3[]) {
    this._points = points
  }

  get radius(): number {
    return this._radius
  }

  @recreateCurve
  set radius(radius: number) {
    this._radius = radius
  }

  get close(): boolean {
    return this._close
  }

  @recreateCurve
  set close(close: boolean) {
    this._close = close
  }

  constructor(points: Vector3[], radius = 20, close = true) {
    super()

    this._points = points
    this._radius = radius
    this._close = close

    this.curves = createRoundedCornersCurve(this._points, this._radius, this._close).curves
  }

  override clone(): this {
    return new (this.constructor as any)(this._points.map(p => p.clone()), this._radius, this._close)
  }

  /**
   * updatePoint
   * @param index
   * @param point
   */
  updatePoint(index: number, point: Vector3): void {
    if (index < 0 || index >= this._points.length)
      throw new Error(`Point index out of range: ${index}`)

    this._points[index] = point
    this.curves = createRoundedCornersCurve(this._points, this._radius, this._close).curves
  }
}